'use client';

import { fetchASPRefreshUserInfo } from '@module/apis/client-side/member';
import type { MemberLoginResponse } from '@module/apis/client-side/member/type';
import SettingConfig from '@module/config/setting';
import { FANZA_MESSAGE_ENABLE_MIN_COIN_USE } from '@module/constants/fanza/message';
import { useMemberLoginInfoQuery } from '@module/queries/member/memberQueryHooks';
import { memberQueryKeys } from '@module/queries/member/memberQueryKeys';
import TimeConstants from '@module/utils/item';
import { useQueryClient } from '@tanstack/react-query';
import throttle from 'lodash/throttle';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { CustomUseQueryOptions } from '../../queries/type';
import { aspCond } from '../conditions/condition';
import { syncLoginInfoQueryData } from './syncLoginInfoQueryData';

type RefetchLoginInfoResult = {
  data: MemberLoginResponse | undefined;
  isError: boolean;
};

const REFETCH_THROTTLE_MS = 1000;

const useGetLoginInfoQuery = (options?: CustomUseQueryOptions<MemberLoginResponse, Error>) => {
  const queryClient = useQueryClient();

  const {
    data: loginInfo,
    isLoading: isLoginInfoLoading,
    isFetching: isLoginInfoFetching,
    refetch,
    isFetched,
    isError,
  } = useMemberLoginInfoQuery(
    {},
    {
      staleTime: TimeConstants.ONE_MINUTE,
      ...options,
    },
  );

  /**
   * Fanza 예외 -> refresh_user_info로 최신 ASP 데이터를 가져온 뒤 memberLoginInfoQuery 캐시를 업데이트한다.
   *
   * Fanza 분기는 react-query refetch가 아니라 fetchASPRefreshUserInfo를 직접 호출하므로
   * react-query의 in-flight 중복제거가 없다. inFlightRef로 응답이 돌아올 때까지 다음 요청을 막는다.
   * (non-fanza는 기존 refetch를 그대로 반환 — 이미 dedup됨)
   */
  const inFlightRef = useRef<Promise<RefetchLoginInfoResult> | null>(null);

  const refetchLoginInfo = useCallback(() => {
    if (!SettingConfig.isFanza) {
      return refetch();
    }

    if (inFlightRef.current) {
      return inFlightRef.current;
    }

    const request = (async (): Promise<RefetchLoginInfoResult> => {
      try {
        const result = await fetchASPRefreshUserInfo();
        syncLoginInfoQueryData(queryClient, result.loginInfo);
        return {
          data: queryClient.getQueryData<MemberLoginResponse>(memberQueryKeys.fetchMemberLoginInfo().queryKey),
          isError: false,
        };
      } catch {
        return {
          data: queryClient.getQueryData<MemberLoginResponse>(memberQueryKeys.fetchMemberLoginInfo().queryKey),
          isError: true,
        };
      } finally {
        inFlightRef.current = null;
      }
    })();

    inFlightRef.current = request;
    return request;
  }, [queryClient, refetch]);

  /**
   * 수동 새로고침 버튼용 — in-flight 가드에 더해 1초 쿨다운(연타 방지)을 건다.
   * 버튼 핸들러는 결과를 쓰지 않으므로 반환값을 버려 `() => Promise<void>`로 노출한다.
   */
  const refetchLoginInfoVoid = useCallback(async () => {
    await refetchLoginInfo();
  }, [refetchLoginInfo]);

  const throttledRefetchLoginInfo = useMemo(
    () => throttle(refetchLoginInfoVoid, REFETCH_THROTTLE_MS, { leading: true, trailing: false }),
    [refetchLoginInfoVoid],
  );

  useEffect(() => () => throttledRefetchLoginInfo.cancel(), [throttledRefetchLoginInfo]);

  const userInfo = loginInfo?.loginInfo?.userInfo;
  const isUserInfo = !!userInfo;
  const isLogin = !!userInfo?.isLogin;
  const isAdult = !!userInfo?.isAdult;
  const isAuthDone = userInfo?.authYN === 'Y';
  const siteMode = loginInfo?.loginInfo?.siteMode;
  const coinHave = userInfo?.coinHave;
  const wasLive = !!userInfo?.liveFirstDateTime && userInfo.liveFirstDateTime !== '0000-00-00 00:00:00';

  let isPostAvailable = true;
  if (aspCond.if('new-asp')) {
    isPostAvailable = SettingConfig.newASPPreset?.aspInfo?.postYN === 'Y' && userInfo?.purchaseUser === 'Y';
  } else if (SettingConfig.isFanza) {
    // fanza는 ハート(coin) 100개 이상 사용한 유저만 쪽지 메뉴 활성화
    isPostAvailable = (userInfo?.coinUse ?? 0) >= FANZA_MESSAGE_ENABLE_MIN_COIN_USE;
  }

  return {
    rawLoginState: userInfo?.isLogin,
    loginInfo,
    isLogin,
    isAdult,
    isUserInfo,
    userInfo,
    isLoginInfoLoading,
    isLoginInfoFetching,
    isFetched,
    isAuthDone,
    refetchLoginInfo,
    throttledRefetchLoginInfo,
    siteMode,
    coinHave,
    wasLive,
    isError,
    isPostAvailable,
  };
};

export default useGetLoginInfoQuery;
